import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AnalyzerView, LabAnalyteView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  Notice,
  Row,
  SearchPicker,
  Table,
  TableBody,
  TableHeader,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { labAnalytesQuery } from '../api/lab.ts';
import { analyzerKeys, putAnalyzerTestCodes } from '../api/lab-analyzers.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './lab.module.css';

// Which analyte each code an analyzer sends is, and the unit it sends (lab connector plan
// §3.2). Only active numeric analytes can be mapped; nothing is converted, so a different
// unit sends the result for review. Codes come from the analyzer's manual or its printout.

interface Mapping {
  analyzerCode: string;
  analyteId: string;
  analyteName: string;
  expectedUnit: string | null;
}

function AnalytePicker({
  analytes,
  status,
  onSelect,
}: {
  analytes: readonly LabAnalyteView[];
  status: 'ready' | 'error' | 'offline' | 'searching';
  onSelect: (analyte: LabAnalyteView) => void;
}) {
  const [text, setText] = useState('');
  const query = text.trim().toLowerCase();
  const matches =
    query === ''
      ? []
      : analytes.filter(
          (a) => a.name.toLowerCase().includes(query) || a.shortName.toLowerCase().includes(query),
        );
  return (
    <SearchPicker
      label="Analyte"
      description="Type part of the name or short name."
      inputValue={text}
      onInputChange={setText}
      status={query === '' ? 'idle' : status}
      items={matches}
      itemsQuery={text.trim()}
      getKey={(a) => a.id}
      getTextValue={(a) => a.name}
      renderItem={(a) => (
        <span>
          {a.name}
          {a.unit === null ? null : <span className={styles.meta}>{` ${a.unit.display}`}</span>}
        </span>
      )}
      onSelect={(a) => {
        setText(a.name);
        onSelect(a);
      }}
      emptyMessage="No numeric analyte matches. Analytes are set up in Lab, Analytes and ranges."
      errorMessage="The analytes couldn't be loaded. Try again."
      offlineMessage="Mapping test codes needs a connection to Wodi."
    />
  );
}

export function AnalyzerTestCodes({
  analyzer,
  editable,
}: {
  analyzer: AnalyzerView;
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const analytes = useQuery({ ...labAnalytesQuery, enabled: editable });
  const numeric = (analytes.data ?? []).filter(
    (a) => a.resultType === 'numeric' && a.status === 'active',
  );
  const [rows, setRows] = useState<Mapping[]>(analyzer.testCodes);
  const [code, setCode] = useState('');
  const [unit, setUnit] = useState('');
  const [analyte, setAnalyte] = useState<LabAnalyteView | null>(null);
  const [pickerKey, setPickerKey] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const dirty = JSON.stringify(rows) !== JSON.stringify(analyzer.testCodes);
  const save = useMutation({
    mutationFn: () =>
      putAnalyzerTestCodes(analyzer.id, {
        expectedVersion: analyzer.version,
        testCodes: rows.map((r) => ({
          analyzerCode: r.analyzerCode,
          analyteId: r.analyteId,
          expectedUnit: r.expectedUnit,
        })),
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: analyzerKeys.all });
      setRows(saved.testCodes);
      toasts.show({ title: 'Test codes saved' });
    },
  });
  const add = () => {
    const trimmed = code.trim();
    if (trimmed === '' || analyte === null) {
      setProblem('Type the analyzer code and choose the analyte.');
      return;
    }
    if (rows.some((r) => r.analyzerCode === trimmed)) {
      setProblem('That code is mapped already. Remove it first to change it.');
      return;
    }
    setProblem(null);
    setRows([
      ...rows,
      {
        analyzerCode: trimmed,
        analyteId: analyte.id,
        analyteName: analyte.name,
        expectedUnit: unit.trim() === '' ? null : unit.trim(),
      },
    ]);
    setCode('');
    setUnit('');
    setAnalyte(null);
    setPickerKey((k) => k + 1);
  };
  return (
    <div className={styles.form}>
      {rows.length === 0 ? (
        <EmptyState>
          No test codes mapped. Every result from this analyzer waits for review.
        </EmptyState>
      ) : (
        <TableScroll>
          <Table aria-label="Test codes" flat>
            <TableHeader>
              <Column isRowHeader>Analyzer code</Column>
              <Column>Analyte</Column>
              <Column>Unit the analyzer sends</Column>
              <Column>
                <span className={styles.meta}>Action</span>
              </Column>
            </TableHeader>
            <TableBody items={rows.map((r) => ({ ...r, id: r.analyzerCode }))}>
              {(r) => (
                <Row id={r.id}>
                  <Cell>
                    <span className="wodi-id">{r.analyzerCode}</span>
                  </Cell>
                  <Cell>{r.analyteName}</Cell>
                  <Cell>{r.expectedUnit ?? "The analyte's own unit, or none"}</Cell>
                  <Cell>
                    {editable ? (
                      <Button
                        dense
                        variant="quiet"
                        aria-label={`Remove code ${r.analyzerCode}`}
                        onPress={() => {
                          setRows(rows.filter((x) => x.analyzerCode !== r.analyzerCode));
                        }}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      )}
      {editable ? (
        <>
          <div className={styles.row}>
            <TextField label="Analyzer code" width="sm" value={code} onChange={setCode} />
            <AnalytePicker
              key={pickerKey}
              analytes={numeric}
              status={
                !online
                  ? 'offline'
                  : analytes.isError
                    ? 'error'
                    : analytes.isPending
                      ? 'searching'
                      : 'ready'
              }
              onSelect={setAnalyte}
            />
            <TextField
              label="Unit the analyzer sends"
              description="Leave empty to use the analyte's unit."
              width="sm"
              value={unit}
              onChange={setUnit}
            />
          </div>
          <div className={styles.actions}>
            <Button onPress={add}>Add code</Button>
            <Button
              variant="primary"
              isDisabled={!dirty || !online}
              isPending={save.isPending}
              pendingLabel="Saving"
              onPress={() => {
                save.mutate();
              }}
            >
              Save test codes
            </Button>
          </div>
          {problem === null ? null : <Notice tone="error">{problem}</Notice>}
          {save.isError ? (
            <Notice tone="error">
              {errorText(save.error, "The test codes weren't saved. Try again.")}
            </Notice>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
